/**
 * ConfirmDialog — BOLT-022: Reusable confirmation dialog.
 *
 * Two tones: "default" (primary confirm) and "danger" (error-colored confirm).
 * Cancel button gets autoFocus on danger tone so destructive action is
 * never auto-pressed.
 */
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  tone?: 'default' | 'danger';
  loading?: boolean;
  /**
   * Failure from the confirmed action, rendered inside the dialog.
   *
   * BOLT-372: without this the dialog swallowed rejections silently — an admin
   * blocked by a 409 clicked Confirm and simply nothing happened, because the
   * caller's `await mutateAsync(...)` threw before its success toast and the
   * dialog had nowhere to show why. Observed live against a scoped admin
   * hitting `out_of_scope_assignments`.
   */
  error?: Error | null;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  tone = 'default',
  loading = false,
  error = null,
}: ConfirmDialogProps) {
  const isDanger = tone === 'danger';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={isDanger ? { color: 'error.main' } : undefined}>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}
        <DialogContentText>{description}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} autoFocus={isDanger} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={onConfirm}
          loading={loading}
          color={isDanger ? 'error' : 'primary'}
          variant="contained"
        >
          {confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
